import { I18n } from 'react-redux-i18n';
import { ADELANTO_TRANSACTION_STATUS } from '../constants';
import { getAdelantoProductId } from './adelantoNexAmount';

export const ADELANTO_NEX_ESTADO = {
    ENVIADO: 'ENVIADO',
    ANULADO: 'ANULADO',
};

export const ADELANTO_CUPO_ESTADO = {
    PENDIENTE: 'PENDIENTE',
    PARCIAL: 'PARCIAL',
    COMPLETO: 'COMPLETO',
    EXCEDIDO: 'EXCEDIDO',
};

const ESTADO_CUPO_I18N_KEY = {
    [ADELANTO_CUPO_ESTADO.PENDIENTE]: 'pendiente',
    [ADELANTO_CUPO_ESTADO.PARCIAL]: 'parcial',
    [ADELANTO_CUPO_ESTADO.COMPLETO]: 'completo',
    [ADELANTO_CUPO_ESTADO.EXCEDIDO]: 'excedido',
};

/** Backend `adelanto_nex.estado_cupo` → localized monitor / export label. */
export const formatEstadoCupoLabel = (estadoCupo) => {
    const token = ESTADO_CUPO_I18N_KEY[estadoCupo];
    if (!token) return null;
    return I18n.t(`contract.adelantoNex.estadoCupo.${token}`);
};

/** Monitor row: backend `estado_cupo` when `adelanto_nex` is present. */
export const resolveRowEstadoCupo = (row) => row?.adelanto_nex?.estado_cupo ?? null;

export const isAdelantoQuotaPending = (estadoCupo) =>
    estadoCupo === ADELANTO_CUPO_ESTADO.PENDIENTE || estadoCupo === ADELANTO_CUPO_ESTADO.PARCIAL;

export const isAdelantoQuotaComplete = (estadoCupo) =>
    estadoCupo === ADELANTO_CUPO_ESTADO.COMPLETO || estadoCupo === ADELANTO_CUPO_ESTADO.EXCEDIDO;

/** CSV / export fields from nested `adelanto_nex` (FR-18). */
export const getVolumeRowAdelantoExportFields = (row, { formatDate } = {}) => {
    const nex = row?.adelanto_nex;
    if (!nex) return null;

    return {
        versionNumero: row?.version_numero ?? '',
        estadoCupo: formatEstadoCupoLabel(nex.estado_cupo) || nex.estado_cupo || '',
        saldoCupo: nex.saldo_cupo_adelanto ?? '',
        fechaAdelanto:
            nex.created_at && formatDate
                ? formatDate(new Date(nex.created_at))
                : nex.created_at ?? '',
        idAdelanto: nex.id ?? '',
    };
};

export const getVersionAdelantosNex = (version) => version?.adelantos_nex ?? [];

export const findAdelantoNexForProduct = (version, productoId) => {
    if (!version) return null;
    const list = getVersionAdelantosNex(version);
    if (productoId != null) {
        const match = list.find((a) => String(a?.producto) === String(productoId));
        if (match) return match;
    }
    return list.find((a) => a?.estado === ADELANTO_NEX_ESTADO.ENVIADO) ?? null;
};

export const findPrimaryAdelantoNex = (version) =>
    findAdelantoNexForProduct(version, getAdelantoProductId(version));

export const mapEstadoCupoToUiStatus = (adelanto) => {
    if (!adelanto || adelanto.estado !== ADELANTO_NEX_ESTADO.ENVIADO) {
        return ADELANTO_TRANSACTION_STATUS.NOT_SENT;
    }
    switch (adelanto.estado_cupo) {
        case ADELANTO_CUPO_ESTADO.PARCIAL:
            return ADELANTO_TRANSACTION_STATUS.PARTIAL;
        case ADELANTO_CUPO_ESTADO.COMPLETO:
            return ADELANTO_TRANSACTION_STATUS.COMPLETED;
        case ADELANTO_CUPO_ESTADO.EXCEDIDO:
            return ADELANTO_TRANSACTION_STATUS.EXCEEDED;
        case ADELANTO_CUPO_ESTADO.PENDIENTE:
        default:
            return ADELANTO_TRANSACTION_STATUS.SENT;
    }
};

const KNOWN_ADELANTO_UI_STATUS = new Set(Object.values(ADELANTO_TRANSACTION_STATUS));

/** Map monitor row adelanto_nex or legacy adelanto_status to UI badge code. */
export const resolveRowAdelantoUiStatus = (row) => {
    if (row?.adelanto_nex) {
        return mapEstadoCupoToUiStatus(row.adelanto_nex);
    }
    const legacy = row?.adelanto_status;
    if (
        legacy &&
        KNOWN_ADELANTO_UI_STATUS.has(legacy) &&
        legacy !== ADELANTO_TRANSACTION_STATUS.NOT_SENT
    ) {
        return legacy;
    }
    return ADELANTO_TRANSACTION_STATUS.NOT_SENT;
};

export const getAdelantoUiStatus = (version, productoId) => {
    const adelanto = productoId
        ? findAdelantoNexForProduct(version, productoId)
        : findPrimaryAdelantoNex(version);
    if (adelanto) {
        return mapEstadoCupoToUiStatus(adelanto);
    }
    const legacy = version?.adelanto_status;
    if (
        legacy &&
        KNOWN_ADELANTO_UI_STATUS.has(legacy) &&
        legacy !== ADELANTO_TRANSACTION_STATUS.NOT_SENT
    ) {
        return legacy;
    }
    return ADELANTO_TRANSACTION_STATUS.NOT_SENT;
};

export const hasAdelantoEnviado = (version, productoId) => {
    const pid = productoId ?? getAdelantoProductId(version);
    if (pid != null) {
        const match = findAdelantoNexForProduct(version, pid);
        if (match) {
            return true;
        }
    }
    if (getVersionAdelantosNex(version).length > 0) {
        return true;
    }
    return (
        !!version?.adelanto_status &&
        version.adelanto_status !== ADELANTO_TRANSACTION_STATUS.NOT_SENT
    );
};

export const getAdelantoUnidadesRegistradas = (version) => {
    const adelanto = findPrimaryAdelantoNex(version);
    if (adelanto?.unidades_registradas != null) {
        return adelanto.unidades_registradas;
    }
    return version?.unidades_registradas ?? 0;
};

export const getAdelantoSaldoCupo = (version) => {
    const adelanto = findPrimaryAdelantoNex(version);
    return adelanto?.saldo_cupo_adelanto ?? null;
};

export const mapTransactionAdelantoFields = (transaction) => {
    const nex = transaction?.adelanto_nex;
    if (!nex) {
        return {
            adelanto_nex: null,
            adelanto_id: transaction?.adelanto_id ?? null,
            adelanto_created_at: null,
            adelanto_status: transaction?.adelanto_status ?? null,
            saldo_cupo: transaction?.volumen?.saldo_cupo ?? null,
        };
    }

    return {
        adelanto_nex: nex,
        adelanto_id: nex.id ?? null,
        adelanto_created_at: nex.created_at ?? null,
        adelanto_status: mapEstadoCupoToUiStatus(nex),
        saldo_cupo: nex.saldo_cupo_adelanto ?? null,
    };
};
